import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { onAuthStateChange, logout } from '../../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Gates the admin area. A user must be (1) signed in AND (2) hold a `payment` or
 * `admin` custom claim — granted centrally from teebook (/system/users → Payment
 * switch). Public payment routes (/pay/:linkId, /guest-payment, /pay/callback*)
 * are NOT wrapped in this component, so the customer payment flow is unaffected.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      if (!user) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      try {
        // force refresh so a just-granted claim is picked up without re-login
        const token = await user.getIdTokenResult(true);
        const ok = token.claims.payment === true || token.claims.admin === true;
        setAuthorized(ok);
        if (!ok) {
          // signed in but no payment access → bounce out cleanly
          await logout();
        }
      } catch {
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!authorized) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
